// 시안 B-2 — 창을 처음 열었을 때 새 요소 위에 뜨는 말풍선. 안내의 tips[] 순서대로 「다음」, 마지막은 「닫기」.
// 닫으면 kind='tip' 으로 기록돼 그 계정에는 다시 안 뜬다. 자리는 화면 코드가 anchor 이름으로 찍는다:
//   <AdminFeatureTip noticeKey="work_deadline_v1" anchor="due-col"><Box as="span">검수 신청 마감</Box></AdminFeatureTip>
// 2026-09-24: 대상이 모달·드로어에 가려져 있으면(대상 중심을 눌러도 대상이 안 눌리는 상태) 말풍선을 띄우지 않고 기다린다 — src/lib/coverage.js
// 2026-09-27 말풍선 전수조사 최종 검수:
//   ① 감싼 요소의 레이아웃을 건드리지 않는다 — 감싸개는 늘 display: contents 이고, 강조 테두리와 말풍선 기준점은 대상 위치에 맞춘
//      고정 위치 상자(Portal)가 맡는다. 예전엔 말풍선이 뜬(또는 기다리는) 동안 감싸개가 inline-block 이라 블록 자식이 줄어들었다.
//   ② 감싼 요소는 다시 마운트되지 않는다(트리 모양 고정) · 첫 그림 전에 가려짐을 본다(처음 값은 「가려짐」).
//   ③ 순번 대신 「안 본 것 중 지금 **그려져 있는** 자리의 첫 말풍선」(pickVisibleTip) — 숨은 탭(display:none) 안 자리는 등록하지 않아
//      뒤 말풍선을 막지 않는다.
import React from "react";
import {
  Popover, PopoverAnchor, PopoverContent, PopoverArrow, PopoverBody, Box, Flex, Text, Button, Portal,
} from "@chakra-ui/react";
import { useAdminFeatureNotices } from "./AdminFeatureNoticeContext";
import { tipsFor, pickVisibleTip } from "../../../lib/adminFeatureNotice";
import { isCovered } from "../../../lib/coverage";

const RING = "0 0 0 2px var(--chakra-colors-brand-500), 0 0 0 6px rgba(60,183,160,0.18)";
const PAD = 4; // 테두리가 글자에 붙지 않게

// 감싸개(display: contents) 안 내용이 그려진 사각형. 안 그려져 있으면(숨은 탭 등) null.
// Range 가 없는 환경(jsdom)은 그려진 것으로 본다(fail-open) — 크기는 0.
export function contentRect(el) {
  if (!el || typeof document === "undefined") return null;
  if (typeof document.createRange !== "function") return { top: 0, left: 0, width: 0, height: 0 };
  const range = document.createRange();
  range.selectNodeContents(el);
  if (typeof range.getClientRects !== "function" || typeof range.getBoundingClientRect !== "function") return { top: 0, left: 0, width: 0, height: 0 };
  if (range.getClientRects().length === 0) return null;
  const r = range.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

// 자리가 지금 그려져 있는지(숨은 탭이면 false) — 500ms 마다
function useDisplayed(ref, active) {
  const [displayed, setDisplayed] = React.useState(false);
  React.useLayoutEffect(() => {
    if (!active) { setDisplayed(false); return undefined; }
    const check = () => setDisplayed(!!contentRect(ref.current));
    check();
    const iv = setInterval(check, 500);
    return () => clearInterval(iv);
  }, [ref, active]);
  return displayed;
}

// 말풍선 차례일 때 자리의 위치·가려짐 — 첫 그림 전에 한 번, 이후 스크롤·크기 변화·300ms 마다. 모르는 동안은 「가려짐」.
function useAnchorBox(ref, active) {
  const [box, setBox] = React.useState({ covered: true, rect: null });
  React.useLayoutEffect(() => {
    if (!active) { setBox({ covered: true, rect: null }); return undefined; }
    let raf = 0;
    const check = () => {
      raf = 0;
      const el = ref.current;
      const rect = contentRect(el);
      if (!rect) { setBox({ covered: true, rect: null }); return; }
      const covered = isCovered(el, rect);
      setBox((prev) => (prev.covered === covered && prev.rect && rect
        && prev.rect.top === rect.top && prev.rect.left === rect.left && prev.rect.width === rect.width && prev.rect.height === rect.height
        ? prev : { covered, rect }));
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    const iv = setInterval(check, 300);
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [ref, active]);
  return box;
}

export default function AdminFeatureTip({ noticeKey, anchor, placement = "bottom-start", children }) {
  const { notices, me, tipSeen, tipMounted, registerTip, tipNext, tipDone } = useAdminFeatureNotices();
  const tips = tipsFor(noticeKey, notices, me);
  const hasTip = tips.some((t) => t.anchor === anchor);
  const wrapRef = React.useRef(null);
  const seen = tipSeen?.[noticeKey] || [];
  // 이번 세션에 이미 넘긴 자리·전부 닫은 안내는 그려졌는지 볼 필요가 없다 — 500ms 확인을 멈춘다
  const displayed = useDisplayed(wrapRef, hasTip && !seen.includes("*") && !seen.includes(anchor));

  // 이 자리가 지금 화면에 그려져 있다고 알린다 — 없거나 숨은 자리의 말풍선은 건너뛰고 다음 말풍선을 띄울 수 있게
  React.useEffect(() => (hasTip && displayed ? registerTip(noticeKey, anchor) : undefined), [hasTip, displayed, noticeKey, anchor, registerTip]);

  const mounted = Object.keys(tipMounted?.[noticeKey] || {});
  const visible = seen.includes("*") ? null : pickVisibleTip(tips, seen, mounted);
  const isOpen = !!visible && visible.anchor === anchor;
  const { covered, rect } = useAnchorBox(wrapRef, isOpen);
  const show = isOpen && !covered && !!rect;

  // 트리 모양은 늘 같다 — Popover › span(display: contents) › children. 테두리·기준점은 Portal 안 고정 상자.
  return (
    <Popover isOpen={show} placement={placement} closeOnBlur={false} isLazy autoFocus={false} returnFocusOnClose={false}>
      <Box ref={wrapRef} as="span" display="contents" data-feature-tip-anchor={anchor}>
        {children}
      </Box>
      {show && (
        <Portal>
          <PopoverAnchor>
            <Box
              position="fixed"
              top={`${rect.top - PAD}px`}
              left={`${rect.left - PAD}px`}
              w={`${rect.width + PAD * 2}px`}
              h={`${rect.height + PAD * 2}px`}
              borderRadius="6px"
              boxShadow={RING}
              pointerEvents="none"
              // 모달(1400) 위 · 말풍선(1500) 아래 — 모달 안 자리(허용시각·작품 등록 창)에서도 테두리가 흰 창 밑에 깔리지 않게.
              // 가려진 자리는 useAnchorBox 가 숨기므로 다른 층 위에 혼자 뜨지 않는다(2026-09-27 재리뷰 2회)
              zIndex={1450}
              data-coachmark-layer
              data-feature-tip-ring={anchor}
            />
          </PopoverAnchor>
          <PopoverContent data-coachmark-layer bg="neutral.800" color="white" border="0" w="270px" boxShadow="lg" _focus={{ boxShadow: "lg" }}>
            <PopoverArrow bg="neutral.800" />
            <PopoverBody px={3} py={2.5}>
              <Text fontSize="12px" lineHeight="1.55">{tips[visible.index].text}</Text>
              <Flex justify="space-between" align="center" mt={2}>
                <Text fontSize="11px" color="neutral.400" fontFamily="mono">{visible.index + 1} / {visible.total}</Text>
                <Flex gap={1}>
                  {!visible.last && (
                    <Button size="xs" variant="ghost" color="neutral.300" _hover={{ bg: "whiteAlpha.200" }} onClick={() => tipDone(noticeKey)}>
                      건너뛰기
                    </Button>
                  )}
                  <Button size="xs" colorScheme="brand" onClick={() => (visible.last ? tipDone(noticeKey) : tipNext(noticeKey, anchor))}>
                    {visible.last ? "닫기" : "다음"}
                  </Button>
                </Flex>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      )}
    </Popover>
  );
}
